import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fingerprint, Trash2 } from 'lucide-react'
import {
  fetchPasskeys,
  passkeysAvailable,
  registerPasskey,
  removePasskey,
  wasCancelled,
} from '../api/passkeys'
import { formatFullDate } from '../api/calendar'

/**
 * Managing the devices that can sign in as you (D-320).
 *
 * **Opt-in, and beside the password rather than instead of it.** A passkey
 * lives on one device: lose the phone and the password or an emailed link is
 * the way back, so removing the password would strand somebody with no route
 * in. Nobody is ever required to set one up.
 *
 * **Not offered on a classroom tablet.** A shared device is unlocked by PIN
 * precisely because it is shared (D-128, D-236), and a passkey on one would
 * sign anybody who picked it up in as whoever registered it. The server refuses
 * it too — this only avoids offering something that would be refused.
 */
export default function PasskeysView({ isKiosk }: { isKiosk: boolean }) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const queryClient = useQueryClient()

  const { data: available } = useQuery({
    queryKey: ['passkeys-available'],
    queryFn: passkeysAvailable,
    staleTime: Infinity,
  })

  const { data: passkeys } = useQuery({
    queryKey: ['passkeys'],
    queryFn: fetchPasskeys,
    enabled: available === true,
  })

  const remove = useMutation({
    mutationFn: removePasskey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passkeys'] }),
    onError: (e: Error) => setError(e.message),
  })

  // Nothing to say on a browser that cannot do it, and nothing to offer on a
  // shared tablet. Both are silence rather than a disabled button explaining
  // itself, because neither is a thing the reader can act on.
  if (!available || isKiosk) return null

  return (
    <section className="mt-8 rounded-2xl border border-line bg-surface px-4 py-3.5">
      <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Face ID and fingerprint
      </h2>

      <p className="mt-1 text-[12.5px] text-ink-3">
        Sign in without typing your password. Your password still works, and still gets you back
        in if you lose this device.
      </p>

      {(passkeys?.length ?? 0) > 0 && (
        <ul className="mt-2.5 divide-y divide-line">
          {passkeys!.map((key) => (
            <li key={key.id} className="flex items-center gap-3 py-2">
              <Fingerprint size={15} strokeWidth={2} className="shrink-0 text-accent" />

              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-semibold wrap-anywhere">
                  {key.nickname ?? 'This device'}
                </span>
                <span className="block text-[11.5px] text-ink-3">
                  {key.lastUsedAt
                    ? `Last used ${formatFullDate(key.lastUsedAt.slice(0, 10))}`
                    : `Added ${formatFullDate(key.createdAt.slice(0, 10))}`}
                </span>
              </span>

              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(key.id)}
                aria-label={`Remove ${key.nickname ?? 'this passkey'}`}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-crit-bg hover:text-crit disabled:opacity-50"
              >
                <Trash2 size={15} strokeWidth={2} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          setError(null)

          try {
            /*
             * Named for the device rather than asked about. A prompt would be
             * one more step in a flow whose whole point is fewer of them, and
             * the browser is about to name the authenticator anyway.
             */
            await registerPasskey(deviceGuess())
            await queryClient.invalidateQueries({ queryKey: ['passkeys'] })
          } catch (e) {
            if (!wasCancelled(e)) setError((e as Error).message)
          } finally {
            setBusy(false)
          }
        }}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-line-2 px-4 py-2.5 text-[13.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2 disabled:opacity-60"
      >
        <Fingerprint size={15} strokeWidth={2.5} />
        {busy ? 'Waiting for your device...' : 'Set up on this device'}
      </button>

      {error && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {error}
        </p>
      )}

      {/*
        Said plainly rather than left to be discovered. "Where did my passkey
        go?" after switching phones is a support call, and one sentence here
        costs nothing.
      */}
      <p className="mt-2 text-[11.5px] text-ink-3">
        A passkey belongs to this device. Set one up on each phone or computer you use.
      </p>
    </section>
  )
}

/**
 * A name for the device, guessed rather than asked for.
 *
 * Deliberately coarse: this is a label in a list of two or three, not
 * identification. Anything that tried to be precise would be reading the user
 * agent to tell somebody something they already know.
 */
function deviceGuess(): string {
  const agent = navigator.userAgent

  if (/iPhone/i.test(agent)) return 'iPhone'
  if (/iPad/i.test(agent)) return 'iPad'
  if (/Android/i.test(agent)) return 'Android phone'
  if (/Macintosh/i.test(agent)) return 'Mac'
  if (/Windows/i.test(agent)) return 'Windows PC'

  return 'This device'
}
